// HTTP client for /api/{consultants/{folder}/workflows,workflows/{id}}.
// Mirrors src/lib/fs/workflows.ts signatures.
import type {
  Workflow,
  WorkflowDocumentSummary,
  WorkflowDocuments,
  WorkflowGapQuestion,
} from "../fs/workflows";
import { api } from "./_client";

interface WorkflowRow {
  id: string;
  consultant_id: string;
  vacancy_id: string;
  status: Workflow["status"];
  match_score: Workflow["match_score"];
  match_rationale: string | null;
  confirmed_requirements: string[] | null;
  gap_questions: WorkflowGapQuestion[] | null;
  documents: WorkflowDocuments | null;
  review_requested: boolean;
  review_requested_at: string | null;
  reviewer_notes: string | null;
  submitted_at: string | null;
  lens_notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// The Workflow interface still exposes `consultant_folder` (legacy string
// identifier). The row's authoritative FK is `consultant_id` (UUID). The
// caller knows the folder from context (they called by folder), so we thread
// it through the mapping function rather than expose consultant_id upstream.
function rowToWorkflow(row: WorkflowRow, consultantFolder: string): Workflow {
  return {
    id: row.id,
    vacancy_id: row.vacancy_id,
    consultant_folder: consultantFolder,
    consultant_oid: row.created_by,
    created_at: row.created_at,
    updated_at: row.updated_at,
    match_score: row.match_score ?? null,
    match_rationale: row.match_rationale,
    confirmed_requirements: row.confirmed_requirements ?? [],
    gap_questions: row.gap_questions ?? [],
    status: row.status,
    review_requested: row.review_requested,
    review_requested_at: row.review_requested_at,
    reviewer_notes: row.reviewer_notes,
    submitted_at: row.submitted_at,
    documents: row.documents ?? {
      tailored_cv: null,
      motivatiebrief: null,
      offerteformulier: null,
    },
    lens_notes: row.lens_notes ?? undefined,
  };
}

function workflowToWrite(wf: Workflow): Partial<WorkflowRow> {
  return {
    status: wf.status,
    match_score: wf.match_score ?? null,
    match_rationale: wf.match_rationale ?? null,
    confirmed_requirements: wf.confirmed_requirements ?? [],
    gap_questions: wf.gap_questions ?? [],
    documents: wf.documents,
    review_requested: wf.review_requested ?? false,
    review_requested_at: wf.review_requested_at ?? null,
    reviewer_notes: wf.reviewer_notes ?? null,
    submitted_at: wf.submitted_at ?? null,
    lens_notes: wf.lens_notes ?? null,
  };
}

// ── Public API — mirrors src/lib/fs/workflows.ts ───────────────────────────

export async function readAllWorkflows(
  _repoPath: string,
  consultantFolder: string,
): Promise<Workflow[]> {
  try {
    const rows = await api<WorkflowRow[]>(
      "GET",
      `/api/consultants/${encodeURIComponent(consultantFolder)}/workflows`,
    );
    return rows.map((r) => rowToWorkflow(r, consultantFolder));
  } catch (err) {
    console.error("readAllWorkflows failed", err);
    return [];
  }
}

export async function createWorkflow(
  _repoPath: string,
  consultantFolder: string,
  _consultantOid: string,
  vacancyId: string,
): Promise<Workflow> {
  const row = await api<WorkflowRow>(
    "POST",
    `/api/consultants/${encodeURIComponent(consultantFolder)}/workflows`,
    { vacancy_id: vacancyId },
  );
  return rowToWorkflow(row, consultantFolder);
}

export async function writeWorkflow(
  _repoPath: string,
  _consultantFolder: string,
  _vacancyId: string,
  workflow: Workflow,
): Promise<void> {
  await api<WorkflowRow>(
    "PUT",
    `/api/workflows/${encodeURIComponent(workflow.id)}`,
    workflowToWrite(workflow),
  );
}

export async function terminateWorkflow(
  _repoPath: string,
  consultantFolder: string,
  vacancyId: string,
): Promise<void> {
  // Find the workflow id for (consultant, vacancy) then delete by id.
  // consultantFolder scopes the lookup so we don't hit a foreign row.
  const rows = await api<WorkflowRow[]>(
    "GET",
    `/api/consultants/${encodeURIComponent(consultantFolder)}/workflows`,
  );
  const target = rows.find((r) => r.vacancy_id === vacancyId);
  if (!target) return;
  await api<void>("DELETE", `/api/workflows/${encodeURIComponent(target.id)}`);
}

// Derived listing — same computation as v1.7 but built on the API list.
export async function listWorkflowDocuments(
  _repoPath: string,
  consultantFolder: string,
): Promise<WorkflowDocumentSummary[]> {
  const workflows = await readAllWorkflows(_repoPath, consultantFolder);
  const summaries: WorkflowDocumentSummary[] = [];
  for (const wf of workflows) {
    const cv = wf.documents?.tailored_cv ?? null;
    const letter = wf.documents?.motivatiebrief ?? null;
    if (!cv && !letter) continue;
    const label = wf.vacancy_id
      .replace(/_\d{3}$/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    summaries.push({
      vacancyId: wf.vacancy_id,
      vacancyLabel: label,
      updatedAt: wf.updated_at,
      tailoredCv: cv,
      coverLetter: letter,
    });
  }
  summaries.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return summaries;
}

// Re-export types so callers importing from api/workflows get the same
// interface surface as they would from fs/workflows.
export type {
  Workflow,
  WorkflowDocument,
  WorkflowDocumentSummary,
  WorkflowDocuments,
  WorkflowGapQuestion,
} from "../fs/workflows";
